import { useEffect, useMemo } from "react";

import { useDisclosure } from "@chakra-ui/react";

import countryList from "country-list";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { useCompany } from "@/hooks/util/useCompany";
import {
  loadPlatformStatus,
  selectPlatformStatusByNameData
} from "@/store/features/clientSettings/connections/connections";
import {
  selectCompany,
  selectCompanyLoading,
  selectCompanyUpdating,
  setCompany,
  updateCompany
} from "@/store/features/companies/companies";
import { industryOptions, toastSuccess } from "@/util/helpers";
import currencyList from "@/utils/minimalCurrencyList";

export const useCompanyPractice = () => {
  const dispatch = useDispatch();
  const vatWarningDisclosure = useDisclosure();
  const { clientId } = useParams();
  const { selectedCompany, selectedClient } = useCompany();

  useEffect(() => {
    if (selectedClient || selectedCompany) {
      dispatch(
        setCompany(
          (selectedClient ? selectedClient?.id : selectedCompany?.id) ?? ""
        )
      );
    }
  }, [dispatch, selectedCompany, selectedClient]);

  const company = useSelector(selectCompany);
  const isCompanyLoading = useSelector(selectCompanyLoading);
  const isCompanyUpdating = useSelector(selectCompanyUpdating);
  const accountingStatus = useSelector(selectPlatformStatusByNameData);

  useEffect(() => {
    if (clientId && company?.accounting_platform) {
      dispatch(loadPlatformStatus(clientId));
    }
  }, [dispatch, clientId, company?.accounting_platform]);

  const initialCountry = useMemo(() => {
    if (!company) return null;
    const defaultCountry = {
      code: "GB",
      name: "United Kingdom"
    };
    return (
      countryList
        .getData()
        .find((country) => country.code === company.reg_country) ||
      defaultCountry
    );
  }, [company]);

  const initialCurrency = useMemo(() => {
    if (!company) return null;
    const defaultCurrency = {
      code: "GBP",
      name: "British Pound"
    };
    return currencyList.get(company.home_currency!) || defaultCurrency;
  }, [company]);

  const initialIndustry = useMemo(() => {
    if (!company) return null;
    return industryOptions.find(
      (industry) => industry.value === company.industry
    );
  }, [company]);

  const { formState, control, watch, getValues, setValue, handleSubmit } =
    useForm({
      values: {
        ...company,
        is_billing_same_as_hq: false,
        industry: initialIndustry,
        reg_country: initialCountry,
        home_currency: initialCurrency
      }
    });

  const currencies = [...Object.values(currencyList.getAll())];
  const isBillingSameAsHQ = watch("is_billing_same_as_hq");

  const handleSaveCompanyDetails = (values: any) => {
    dispatch(
      updateCompany(
        selectedClient ? selectedClient?.id : (selectedCompany?.id ?? ""),
        {
          ...values,
          reg_country: values.reg_country.code,
          home_currency: values.home_currency.code,
          industry: values.industry?.value,
          billing_address: values.is_billing_same_as_hq
            ? values.hq_address
            : values.billing_address
        },
        () => {
          toastSuccess("Company details updated successfully");
        }
      )
    );
  };

  /**
   * The function `checkVatRegistrationStatus` checks for conflicts in VAT registration status and
   * triggers a warning if necessary.
   *
   * If the company has a valid accounting platform integration but the new choice of the VAT registration differs
   * from the status indicated by the accounting platform, we need to trigger a warning. If not, we can proceed with the change.
   *
   * @param onChange - The `onChange` function takes a `value` of type string as an argument.
   * @param {string} newValue - The `newValue` will be checked against the existing VAT registration status.
   */
  const checkVatRegistrationStatus = (
    onChange: (value: string) => void,
    newValue: string
  ) => {
    const choiceHasConflict =
      accountingStatus?.company_status === "valid" &&
      String(accountingStatus?.is_vat_registered) !== newValue;

    if (choiceHasConflict) {
      vatWarningDisclosure.onOpen();
    } else {
      onChange(newValue);
    }
  };

  const updateVATStatus = () => {
    const values = getValues();
    setValue("is_vat_registered", !values.is_vat_registered);
    vatWarningDisclosure.onClose();
  };

  const sortedCountries = useMemo(
    () => countryList.getData().sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  return {
    company,
    isCompanyLoading,
    isCompanyUpdating,
    initialCountry,
    initialCurrency,
    currencies,
    countries: sortedCountries,
    isBillingSameAsHQ,
    formState,
    control,
    accountingStatus,
    vatWarningDisclosure,
    getValues,
    handleSubmit,
    updateVATStatus,
    handleSaveCompanyDetails,
    checkVatRegistrationStatus
  };
};
